// "email me a code" plus an 8-digit input. accounts without a password confirm
// a sensitive change with it: the code proves the person holding this session
// also reads the account's mailbox. the caller sends the proof along with the
// change itself, so there is no separate step-up token to keep.
import React, { useCallback, useState } from "react";
import { useRequestStepUpMutation } from "../../services/rtkApi/authServiceApi";
import type {
  StepUpAction,
  StepUpChallenge,
  StepUpProof,
} from "../../services/rtkApi/authServiceApi";

const DEFAULT_EXPIRES_SECONDS = 15 * 60;

// people paste "1234 5678" or "1234-5678" straight from the email
export function toEmailCode(input: string): string | null {
  const code = input.replace(/[\s-]/g, "");
  return /^\d{8}$/.test(code) ? code : null;
}

type AuthServiceError = {
  status?: number | string;
  data?: { code?: string; error?: string; message?: string };
};

// plain copy for the refusals auth gives around step-up, so a person knows
// whether to wait, try again, or do something else
export function stepUpErrorMessage(error: unknown, fallback: string): string {
  const err = (error ?? {}) as AuthServiceError;
  const code = err.data?.code;
  if (code === "email_unavailable" || err.status === 503) {
    return "Email isn't working on our side right now. The admin has been told. Please try again later.";
  }
  if (code === "rate_limited" || err.status === 429) {
    return "Too many tries. Wait a few minutes and try again.";
  }
  if (code === "step_up_required") {
    return "Confirm this with a code from your email first.";
  }
  if (code === "password_login_disabled") {
    return "Password sign-in is turned off. Sign in with an email code instead.";
  }
  return err.data?.message || err.data?.error || fallback;
}

export function useStepUp(action: StepUpAction) {
  const [requestStepUp, { isLoading: sending }] = useRequestStepUpMutation();
  const [challenge, setChallenge] = useState<StepUpChallenge | null>(null);
  const [error, setError] = useState("");

  const requestCode = useCallback(async (): Promise<StepUpChallenge | null> => {
    setError("");
    try {
      const next = await requestStepUp({ action }).unwrap();
      setChallenge(next);
      return next;
    } catch (err) {
      setError(stepUpErrorMessage(err, "We couldn't send a code. Please try again."));
      return null;
    }
  }, [action, requestStepUp]);

  // the proof for the latest code sent, once the input holds all 8 digits
  const proofFor = useCallback(
    (input: string): StepUpProof | null => {
      const code = toEmailCode(input);
      if (!challenge || !code) return null;
      return { stepUpChallengeId: challenge.challengeId, stepUpCode: code };
    },
    [challenge],
  );

  return { challenge, sending, error, requestCode, proofFor };
}

interface StepUpCodeFieldProps {
  action: StepUpAction;
  // gets the proof once a full code is typed, and null whenever it stops being
  // usable (the input changed, or a newer code replaced it)
  onProofChange: (proof: StepUpProof | null) => void;
  disabled?: boolean;
  className?: string;
}

const StepUpCodeField: React.FC<StepUpCodeFieldProps> = ({
  action,
  onProofChange,
  disabled = false,
  className,
}) => {
  const { challenge, sending, error, requestCode, proofFor } = useStepUp(action);
  const [code, setCode] = useState("");

  const handleSend = async () => {
    const next = await requestCode();
    if (next) {
      // only the newest code is worth sending, so drop whatever was typed
      setCode("");
      onProofChange(null);
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    onProofChange(proofFor(value));
  };

  const minutes = Math.round((challenge?.expiresInSeconds || DEFAULT_EXPIRES_SECONDS) / 60);

  return (
    <div className={className}>
      {challenge ? (
        <>
          <p>
            We sent a code to {challenge.sentTo || "your email"}. It works for {minutes} minutes.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="8-digit code"
            aria-label="Code from the email"
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            disabled={disabled}
          />
          <button type="button" onClick={handleSend} disabled={disabled || sending}>
            {sending ? "Sending..." : "Send a new code"}
          </button>
        </>
      ) : (
        <button type="button" onClick={handleSend} disabled={disabled || sending}>
          {sending ? "Sending..." : "Email me a code"}
        </button>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  );
};

export default StepUpCodeField;
